export interface CalendarEvent {
  title: string
  description: string
  location: string
  /** ISO with the vendor offset. */
  start: string
  end: string
  uid: string
}

/**
 * Both calendar formats want UTC basic-format stamps (20260915T033000Z). The
 * slot strings already carry the vendor offset, so `Date` converts them
 * correctly here — this is the one place where an absolute instant, not a wall
 * clock, is what the file needs.
 */
function toUtcStamp(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')
}

function escapeIcs(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n')
}

/** A data: URL holding the whole .ics — no library, no server round trip. */
export function icsDataUrl(event: CalendarEvent): string {
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//VegaBooking//Storefront//VI',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${event.uid}`,
    `DTSTAMP:${toUtcStamp(new Date().toISOString())}`,
    `DTSTART:${toUtcStamp(event.start)}`,
    `DTEND:${toUtcStamp(event.end)}`,
    `SUMMARY:${escapeIcs(event.title)}`,
    `DESCRIPTION:${escapeIcs(event.description)}`,
    `LOCATION:${escapeIcs(event.location)}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ]
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(lines.join('\r\n'))}`
}

export function googleCalendarUrl(event: CalendarEvent): string {
  const params = new URLSearchParams({
    action: 'TEMPLATE',
    text: event.title,
    details: event.description,
    location: event.location,
    dates: `${toUtcStamp(event.start)}/${toUtcStamp(event.end)}`,
  })
  return `https://calendar.google.com/calendar/render?${params.toString()}`
}
